import React, { createContext, useContext, useState, useCallback } from 'react'

import { useGlobalDataStore } from '@stores/globalData/globalData.store'
import { isEmpty } from '@utils/helpers'
import nfsyn from '@utils/nf-webaudio/nf-webaudio'

import type { SynthesisContextType } from '@utils/nf-webaudio/synthesis-context'
import type { SynthContextOptions } from '@utils/nf-webaudio/synthesis-context'
import type { FC } from 'react'

export type AudioContextContextType = {
  setUpAudioContext: () => void
  audioContext: SynthesisContextType | null
}

export const AudioContextContext = createContext<AudioContextContextType>({
  setUpAudioContext: () => null,
  audioContext: null,
})

/**
 * This setups a singleton contextual object for our web-audio context
 * In its initialization
 *  - it loads up the instrument manifest (which stores that paths for all of instruments we use)
 *  - connects the master gain node to the context
 *  - connects reverb to the context
 *
 * The provider is here so that React components can have easy access to it
 * The provider is passed down on page load so everything is ready to go!!
 */
const AudioContextProvider: FC = ({ children }) => {
  const [audioContext, setAudioContext] = useState<SynthesisContextType | null>(null)

  const contextValue = {
    setUpAudioContext: useCallback(async () => {
      // Gets bootstrapped data passed down from the server
      const serverData = useGlobalDataStore.getState().serverData

      if (!serverData || isEmpty(serverData)) {
        console.error('ServerData object is empty')
        return
      }

      const { instrument_prefix } = serverData

      if (!instrument_prefix) {
        console.error('Instrument prefix cannot be found')
        return
      }

      // Retrieve the manifest from the server
      const manifest = useGlobalDataStore.getState().assetManifest
      if (!manifest) {
        console.error('Manifest cannot be found')
        return
      }

      const instrumentManifestPath = manifest['instruments/manifest.xml']

      if (!instrumentManifestPath) {
        console.error('Instrument manifest path cannot be found')
        return
      }

      // Using the paths inside the manifest construct a url make a request to retrieve instrument xml
      const instrumentXmlPath = `${serverData.assetPath}/client/${instrumentManifestPath}`

      const options: SynthContextOptions = {
        instrumentBase: instrument_prefix,
        manifestLocation: instrumentXmlPath,
        premium: true,
        preview: true,
        offline: false,
      }

      if (isEmpty(nfsyn) && !('SynthesisContextSingleton' in nfsyn)) {
        console.error('nfsyn cannot be found')
        return
      }

      const instance: SynthesisContextType = await nfsyn.SynthesisContextSingleton.getInstance(
        options
      )
      setAudioContext(instance)
    }, []),
    audioContext,
  }

  return (
    <AudioContextContext.Provider value={contextValue}>{children}</AudioContextContext.Provider>
  )
}

export default AudioContextProvider

export const useAudioContext: () => AudioContextContextType = () => useContext(AudioContextContext)
